"use client"

import { usePathname } from 'next/navigation';
import { useMemo, useSyncExternalStore } from 'react';
import { useBreadcrumbEntity } from '@/components/breadcrumbs/breadcrumb-entity-context';
import { locales } from '@/i18n/config';

export type BreadcrumbEntityType = 'shop' | 'charity' | 'project' | 'merchant' | 'user' | 'category' | 'transaction';

export type BreadcrumbItem = {
  title: string;
  link: string;
  entityType?: BreadcrumbEntityType;
  entityId?: string;
  charityId?: string;
};

const SEGMENT_LABELS: Record<string, string> = {
  admin: "Admin",
  shops: "Shops",
  charities: "Charities",
  merchants: "Merchants",
  users: "Users",
  categories: "Kategorien",
  "amazon-statistics": "Amazon Statistiken",
  transactions: "Transaktionen",
  "discount-codes": "Discount-Codes",
  projects: "Projekte",
  new: "Neu",
};

const SEGMENT_TO_ENTITY: Record<string, BreadcrumbEntityType> = {
  shops: 'shop',
  charities: 'charity',
  projects: 'project',
  merchants: 'merchant',
  users: 'user',
  categories: 'category',
  transactions: 'transaction',
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function segmentLabel(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  if (UUID_REGEX.test(segment)) return "Details";
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

/** Key used by ShopBrowser to persist the last visited /shops URL with filters. */
const SHOPS_RETURN_URL_KEY = "donista.shopsReturnUrl";

export function useBreadcumbs(): BreadcrumbItem[] {
  const pathname = usePathname();
  const entity = useBreadcrumbEntity();
  const entityName = entity?.name;

  // Read the last /shops URL stored by ShopBrowser so the breadcrumb link
  // preserves the active category/search/page when navigating back from a shop detail page.
  // useSyncExternalStore with a no-op subscribe and "/shops" server snapshot avoids hydration mismatch.
  const shopsReturnUrl = useSyncExternalStore(
    () => () => {},
    () => sessionStorage.getItem(SHOPS_RETURN_URL_KEY) ?? "/shops",
    () => "/shops",
  );

  return useMemo(() => {
    const segments = pathname.split('/').filter(Boolean).filter(s => !(locales as readonly string[]).includes(s));
    const items = segments.map((segment, index): BreadcrumbItem => {
      const path = `/${segments.slice(0, index + 1).join('/')}`;
      // For /shops, use the stored return URL (with category/search/page) if available.
      const resolvedPath = segment === "shops" && !segments.includes("admin") ? shopsReturnUrl : path;
      const isUuid = UUID_REGEX.test(segment);

      if (isUuid) {
        const parentSegment = index > 0 ? segments[index - 1] : undefined;
        const entityType = parentSegment ? SEGMENT_TO_ENTITY[parentSegment] : undefined;
        const charityId = entityType === 'project'
          ? segments.slice(0, index).find(s => UUID_REGEX.test(s))
          : undefined;
        return { title: "Details", link: resolvedPath, entityType, entityId: segment, charityId };
      }

      return { title: segmentLabel(segment), link: resolvedPath };
    });

    // When the page publishes an entity name via context (shop/project
    // slug routes), override the *last* breadcrumb title with that name
    // and strip any hook-based lookup hints so the breadcrumb component
    // renders the provided name directly without re-fetching.
    if (entityName && items.length > 0) {
      const lastIndex = items.length - 1;
      const last = items[lastIndex]!;
      items[lastIndex] = {
        ...last,
        title: entityName,
        entityType: undefined,
        entityId: undefined,
        charityId: undefined,
      };
    }

    return items;
  }, [pathname, entityName, shopsReturnUrl]);
}
